import { track } from "@/lib/analytics"
import { newRequestId } from "@/lib/requestIds"
import { EngagementView, summaryOf, type EngagementPayload, type ExitReason } from "./engagement"
import { wordsBucket } from "./engagementMetrics"

export const FLUSH_MS = 15_000
const SHOWN = 0.5

/** What the manager needs to know of a turn (a slice of the turn store and the answer session). */
export interface TurnFacts {
  turnId: string
  status: string
  hasHead: boolean
  mode: string
  sources: number
  requestId?: string
  superseded?: boolean
}

export interface ManagerDeps {
  threadId: string
  /** Signed-in, non-guest threads only: when false the manager does nothing and never sends. */
  enabled: boolean
  send: (p: EngagementPayload, sync: boolean) => void
  now?: () => number
  newId?: () => string
}

interface Entry {
  key: string
  v: EngagementView
  el: HTMLElement | null
  body: HTMLElement | null
  groups: HTMLElement[][]
  shown: Map<Element, boolean>
  io: IntersectionObserver | null
  summarySent: boolean
}

const BLOCKS = "h1,h2,h3,h4,p,li,table,blockquote"
const HEADING = /^H[1-4]$/
const wordsIn = (el: Element): number => (el.textContent ?? "").split(/\s+/).filter(Boolean).length

/**
 * Sections of a rendered answer: its paragraphs, list items, tables and quotes in order, with a heading
 * joined to the block that follows it. Nested blocks (a paragraph in a list item, anything in a table)
 * count once, as the outer one. Words are counted here from textContent and never leave the browser.
 */
export function sectionGroups(body: HTMLElement): HTMLElement[][] {
  const roots = body.querySelectorAll<HTMLElement>(".averdict, .asecs")
  const all: HTMLElement[] = []
  roots.forEach((r) => r.querySelectorAll<HTMLElement>(BLOCKS).forEach((e) => all.push(e)))
  const blocks = all.filter((e) => {
    if (e.parentElement?.closest("table, li, blockquote")) return false
    return Boolean((e.textContent ?? "").trim())
  })
  const groups: HTMLElement[][] = []
  let pending: HTMLElement[] = []
  for (const e of blocks) {
    if (HEADING.test(e.tagName)) { pending.push(e); continue }
    groups.push([...pending, e])
    pending = []
  }
  if (pending.length) groups.push(pending)
  return groups
}

/** Reading-behaviour tracking for every answer block on the page; the hook creates one per thread. */
export class EngagementManager {
  private entries = new Map<string, Entry>()
  private activeKey: string | null = null
  private timer = 0
  private frame = 0
  private scrollAt = 0
  private rescanTimer = 0
  private mo: MutationObserver | null = null
  private hovering = new Map<Element, number>()
  private selecting = false
  private started = false
  private readonly now: () => number
  private readonly newId: () => string

  constructor(private readonly deps: ManagerDeps) {
    this.now = deps.now ?? (() => Date.now())
    this.newId = deps.newId ?? newRequestId
  }

  // --- turns -----------------------------------------------------------------------------------

  /** Reconcile with the page's turns: start a view for a new answer, mark its stream complete. */
  syncTurns(order: string[], turns: Record<string, TurnFacts | undefined>): void {
    if (!this.deps.enabled) return
    const t = this.now()
    for (const key of order) {
      const f = turns[key]
      if (!f || f.superseded) continue
      let e = this.entries.get(key)
      if (!e) {
        const running = f.status === "running"
        if (!running && !(f.hasHead && (f.status === "done" || f.status === "error"))) continue
        const v = new EngagementView({
          viewId: this.newId(), sessionId: this.deps.threadId, turnKey: f.turnId || key,
          streamed: running, now: t, requestId: f.requestId ?? null, mode: f.mode || null,
          active: this.pageActive() && this.isActive(key), visible: this.pageVisible(),
        })
        e = { key, v, el: null, body: null, groups: [], shown: new Map(), io: null, summarySent: false }
        this.entries.set(key, e)
      }
      e.v.mode = f.mode || e.v.mode
      e.v.requestId = f.requestId ?? e.v.requestId
      e.v.setSourcesCount(f.sources)
      if (e.v.isStreaming && f.status !== "running") {
        if (f.hasHead) e.v.streamComplete(t)
        else this.drop(e) // a clarify card or an early error: no answer to read
      }
    }
    this.applyActivity()
    this.scheduleRescan()
  }

  /** The turn the reader is on (the page's scroll-spy). Time and scroll accrue to this one only. */
  setActiveTurn(key: string | null): void {
    if (!this.deps.enabled || key === this.activeKey) return
    const prev = this.activeKey && this.entries.get(this.activeKey)
    this.activeKey = key
    this.applyActivity()
    if (prev && !prev.v.isEnded) this.flush(prev, false, false)
  }

  /** The reader sent a follow-up from this answer: the answer in view ends with that reason. */
  followup(): void {
    if (!this.deps.enabled) return
    const e = this.current()
    if (e) this.finish(e, "followup", false)
  }

  /** The reader pressed stop on a running stream. */
  stop(): void {
    const t = this.now()
    for (const e of this.entries.values()) e.v.stop(t)
  }

  // --- lifecycle -------------------------------------------------------------------------------

  start(): void {
    if (!this.deps.enabled || this.started || typeof window === "undefined") return
    this.started = true
    window.addEventListener("scroll", this.onScroll, { passive: true })
    window.addEventListener("focus", this.applyActivity)
    window.addEventListener("blur", this.applyActivity)
    window.addEventListener("pagehide", this.onPageHide)
    document.addEventListener("visibilitychange", this.onVisibility)
    document.addEventListener("selectionchange", this.onSelection)
    document.addEventListener("copy", this.onCopy)
    document.addEventListener("click", this.onClick, true)
    document.addEventListener("keydown", this.onInteract, true)
    document.addEventListener("pointerdown", this.onInteract, true)
    document.addEventListener("mouseover", this.onOver)
    document.addEventListener("mouseout", this.onOut)
    this.timer = window.setInterval(this.onTick, FLUSH_MS)
    if (typeof MutationObserver !== "undefined") {
      this.mo = new MutationObserver(() => this.scheduleRescan())
      this.mo.observe(document.body, { childList: true, subtree: true, characterData: true })
    }
  }

  /** Leaving the page's thread (route change or unmount): every open view ends as "navigate". */
  dispose(): void {
    if (!this.deps.enabled) return
    for (const e of this.entries.values()) this.finish(e, "navigate", false)
    this.teardown()
  }

  private teardown(): void {
    if (!this.started) return
    this.started = false
    window.removeEventListener("scroll", this.onScroll)
    window.removeEventListener("focus", this.applyActivity)
    window.removeEventListener("blur", this.applyActivity)
    window.removeEventListener("pagehide", this.onPageHide)
    document.removeEventListener("visibilitychange", this.onVisibility)
    document.removeEventListener("selectionchange", this.onSelection)
    document.removeEventListener("copy", this.onCopy)
    document.removeEventListener("click", this.onClick, true)
    document.removeEventListener("keydown", this.onInteract, true)
    document.removeEventListener("pointerdown", this.onInteract, true)
    document.removeEventListener("mouseover", this.onOver)
    document.removeEventListener("mouseout", this.onOut)
    window.clearInterval(this.timer)
    window.clearTimeout(this.rescanTimer)
    window.cancelAnimationFrame(this.frame)
    this.mo?.disconnect()
    for (const e of this.entries.values()) e.io?.disconnect()
  }

  // --- flushing --------------------------------------------------------------------------------

  private flush(e: Entry, final: boolean, sync: boolean): void {
    const t = this.now()
    this.refreshWords(e)
    if (e.v.engagedNow(t)) {
      const s = summaryOf(e.v.snapshot(t))
      track("answer_engaged", { mode: e.v.mode, coverage: round2(s.coverage), dwell_ms: e.v.snapshot(t).dwell_ms })
    }
    const p = e.v.snapshot(t, final)
    try { this.deps.send(p, sync) } catch { /* telemetry never throws into the UI */ }
    if (final && !e.summarySent) {
      e.summarySent = true
      const s = summaryOf(p)
      track("answer_view_summary", {
        mode: p.mode,
        answer_words_bucket: wordsBucket(p.answer_words),
        dwell_ms: p.dwell_ms,
        reading_effort: s.effort === null ? null : round2(s.effort),
        coverage: round2(s.coverage),
        reread_ratio: round2(s.reread),
        load_score_base: s.loadScoreBase,
        aborted: p.aborted,
        abort_phase: p.abort_phase,
        exit: p.exit,
      })
      if (p.abort_phase === "streaming") track("answer_stream_aborted", { phase: "streaming", mode: p.mode })
    }
  }

  private finish(e: Entry, reason: ExitReason, sync: boolean): void {
    if (e.v.isEnded) return
    e.v.end(reason, this.now())
    this.flush(e, true, sync)
    e.io?.disconnect()
    e.io = null
  }

  private drop(e: Entry): void {
    e.io?.disconnect()
    this.entries.delete(e.key)
  }

  private onTick = (): void => {
    const e = this.current()
    if (e && !e.v.isEnded && this.pageVisible()) this.flush(e, false, false)
  }

  private onPageHide = (): void => {
    for (const e of this.entries.values()) this.finish(e, "close", true)
  }

  private onVisibility = (): void => {
    const t = this.now()
    const visible = this.pageVisible()
    for (const e of this.entries.values()) e.v.setVisible(visible, t)
    this.applyActivity()
    if (!visible) for (const e of this.entries.values()) if (!e.v.isEnded && this.isActive(e.key)) this.flush(e, false, true)
  }

  // --- DOM wiring ------------------------------------------------------------------------------

  private pageVisible = (): boolean => typeof document === "undefined" || document.visibilityState !== "hidden"
  private pageActive = (): boolean => this.pageVisible() && (typeof document === "undefined" || document.hasFocus())

  private current(): Entry | undefined {
    if (this.activeKey && this.entries.has(this.activeKey)) return this.entries.get(this.activeKey)
    let last: Entry | undefined
    for (const e of this.entries.values()) if (!e.v.isEnded) last = e
    return last
  }

  private isActive(key: string): boolean {
    return this.current()?.key === key
  }

  private applyActivity = (): void => {
    const t = this.now()
    const page = this.pageActive()
    for (const e of this.entries.values()) e.v.setActive(page && this.isActive(e.key), t)
  }

  private entryOf(node: Node | null): Entry | undefined {
    const el = node instanceof Element ? node : node?.parentElement
    const key = el?.closest<HTMLElement>("[data-turn]")?.dataset.turn
    return key ? this.entries.get(key) : undefined
  }

  private inBody(e: Entry | undefined, node: Node | null): e is Entry {
    return Boolean(e?.body && node && e.body.contains(node))
  }

  private scheduleRescan(): void {
    if (typeof window === "undefined" || !this.started) return
    window.clearTimeout(this.rescanTimer)
    this.rescanTimer = window.setTimeout(() => this.rescan(), 150)
  }

  private rescan(): void {
    for (const e of this.entries.values()) {
      if (e.v.isEnded) continue
      const el = document.getElementById(`turn-${e.key}`)
      const body = el?.querySelector<HTMLElement>("[data-answer-body]") ?? null
      e.el = el
      e.body = body
      if (!body) continue
      const groups = sectionGroups(body)
      const flat = new Set(groups.flat())
      const prev = new Set(e.groups.flat())
      const same = flat.size === prev.size && [...flat].every((x) => prev.has(x)) && groups.length === e.groups.length
      e.groups = groups
      if (same) continue
      if (typeof IntersectionObserver !== "undefined") {
        if (!e.io) e.io = new IntersectionObserver((ents) => this.onIntersect(e, ents), { threshold: Array.from({ length: 21 }, (_, i) => i / 20) })
        for (const x of prev) if (!flat.has(x)) { e.io.unobserve(x); e.shown.delete(x) }
        for (const x of flat) if (!prev.has(x)) e.io.observe(x)
      }
      this.refreshWords(e)
      this.pushShown(e)
    }
  }

  private refreshWords(e: Entry): void {
    e.v.setSections(e.groups.map((g) => g.reduce((a, x) => a + wordsIn(x), 0)), this.now())
  }

  private onIntersect(e: Entry, ents: IntersectionObserverEntry[]): void {
    for (const x of ents) {
      const room = Math.min(x.boundingClientRect.height, x.rootBounds?.height ?? window.innerHeight) || 1
      e.shown.set(x.target, x.isIntersecting && x.intersectionRect.height / room >= SHOWN)
    }
    this.pushShown(e)
    this.measureDepth(e)
  }

  private pushShown(e: Entry): void {
    const on: number[] = []
    e.groups.forEach((g, i) => { if (g.some((x) => e.shown.get(x))) on.push(i) })
    e.v.setOnScreen(on, this.now())
  }

  private measureDepth(e: Entry): void {
    if (!e.body) return
    const r = e.body.getBoundingClientRect()
    if (r.height <= 0) return
    e.v.setDepth(((window.innerHeight - r.top) / r.height) * 100, this.now())
  }

  private onScroll = (): void => {
    if (this.frame) return
    this.frame = window.requestAnimationFrame(() => {
      this.frame = 0
      const t = this.now()
      if (t - this.scrollAt < 100) return
      this.scrollAt = t
      const e = this.current()
      if (!e || e.v.isEnded) return
      e.v.scroll(window.scrollY, t)
      this.measureDepth(e)
    })
  }

  private onInteract = (ev: Event): void => {
    const e = this.entryOf(ev.target as Node | null) ?? this.current()
    if (e && !e.v.isEnded) e.v.interact(this.now())
  }

  private onSelection = (): void => {
    const sel = document.getSelection()
    if (!sel || sel.isCollapsed || !sel.toString()) { this.selecting = false; return }
    if (this.selecting) return
    const e = this.entryOf(sel.anchorNode)
    if (!this.inBody(e, sel.anchorNode) || e.v.isEnded) return
    this.selecting = true
    e.v.selection(this.now())
  }

  private onCopy = (): void => {
    const sel = document.getSelection()
    const e = this.entryOf(sel?.anchorNode ?? null)
    if (this.inBody(e, sel?.anchorNode ?? null) && !e.v.isEnded) e.v.copy(this.now())
  }

  private cite(ev: Event): { e: Entry; el: Element } | null {
    const el = (ev.target as Element | null)?.closest?.(".cite")
    if (!el) return null
    const e = this.entryOf(el)
    return this.inBody(e, el) && !e.v.isEnded ? { e, el } : null
  }

  private onClick = (ev: Event): void => {
    const c = this.cite(ev)
    if (c) c.e.v.citationClick(this.now())
  }

  private onOver = (ev: Event): void => {
    const c = this.cite(ev)
    if (c && !this.hovering.has(c.el)) this.hovering.set(c.el, this.now())
  }

  private onOut = (ev: Event): void => {
    const c = this.cite(ev)
    const from = c ? this.hovering.get(c.el) : undefined
    if (c && from !== undefined) {
      this.hovering.delete(c.el)
      c.e.v.citationHover(this.now() - from)
    }
  }
}

const round2 = (n: number) => Math.round(n * 100) / 100
